import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useQuery, useMutation } from '@tanstack/react-query'
import { DataGrid, GridRowSelectionModel } from '@mui/x-data-grid'
import dayjs from 'dayjs'
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Button,
  Snackbar,
  Popper,
  ClickAwayListener,
} from '@mui/material'
import { ArrowDropDown } from '@mui/icons-material'
import { useWatch, useForm, FormProvider } from 'react-hook-form'
import {
  fetchCampaign,
  fetchFlightStores,
  updateAvailableZones,
  saveFlightTargets,
  updateCreative,
  deleteCreative,
  updateTargetingPreview,
  fetchStoreProperties,
  fetchContentCategories,
  gtvOwnerId
} from '../getData'
import {
  CreativePreviewInput,
  CreativeUpdateInput,
  CreativeUploadResponse,
  ErrorResponse,
  Flight,
  Store,
  ZoneResponse,
  PreviewResponse,
  CampaignStatus,
  UserAuthResponse,
  FlightTargetSaveInput
} from '../../types/campaign'
import { US_STATES } from '../../types/constants'
import theme from '../theme'
import FlightZones from '../FlightZones'
import TwoColumnLayout from '../TwoColumnLayout'
import FlightSidebar from '../FlightSidebar'
import NewFlightIcon from '../icons/NewFlight'
import CircularLoader from '../CircularLoader'
import ConfirmationModal from '../ConfirmationModal'
import TargetingFilters from '../TargetingFilters'
import FilterPill from '../FilterPill'

type TargetingFiltersForm = {
  selectedPropertyValues: number[]
  selectedBanners: number[]
  selectedStates: string[]
}

const FlightTargets = ({ user, isBannerExpanded = false, isStateExpanded = false }: { user: UserAuthResponse | null, isBannerExpanded?: boolean, isStateExpanded?: boolean }) => {
  const navigate = useNavigate()
  const isGtvUser = user ? user?.company.id === gtvOwnerId : false
  const { id: campaignId, flightId } = useParams()

  const formMethods = useForm<TargetingFiltersForm>({
    defaultValues: {
      selectedPropertyValues: [],
      selectedBanners: [],
      selectedStates: []
    }
  })
  const { setValue, control } = formMethods

  const [currentFlight, setCurrentFlight] = useState<Flight>()
  const [searchTerm, setSearchTerm] = useState('')
  const [showSelected, setShowSelected] = useState(false)
  const [exactIdMatch, setExactIdMatch] = useState(false)
  const [selectedStores, setSelectedStores] = useState<number[]>([])
  const [currentGridSelection, setCurrentGridSelection] = useState<number[]>([])
  const [targetingInput, setTargetingInput] =
    useState<CreativePreviewInput | null>(null)
  const [zonesResponse, setZonesResponse] = useState<ZoneResponse | null>(null)
  const [previewZones, setPreviewZones] = useState<number[] | null>(null)
  const [previewPlacements, setPreviewPlacements] = useState<number[] | null>(null)
  const [previewResponse, setPreviewResponse] = useState<PreviewResponse | null>(null)
  const [openSnackbar, setOpenSnackbar] = useState({ open: false, message: '' })
  const [confirmationModal, setConfirmationModal] = useState({
    open: false,
    message: ''
  })
  const [outdatedCreatives, setOutdatedCreatives] = useState<
    CreativeUploadResponse[]
  >([])
  const [creativeAutoUpdateError, setCreativeAutoUpdateError] = useState(false)

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [expandedPanels, setExpandedPanels] = React.useState<string[]>([])
  const selectedPropertyValues = useWatch({ control, name: 'selectedPropertyValues', defaultValue: [] })
  const selectedBanners = useWatch({ control, name: 'selectedBanners', defaultValue: [] })
  const selectedStates = useWatch({ control, name: 'selectedStates', defaultValue: [] })

  const { data: fetchedCampaign, refetch: refetchCampaign } = useQuery({
    queryKey: ['campaign', campaignId],
    queryFn: () => fetchCampaign(Number(campaignId))
  })

  const {
    data: flightStores,
    isPending: isLoadingStores,
    isError: isErrorLoadingStores,
    isSuccess: isSuccessLoadingStores
  } = useQuery({
    queryKey: ['flightStores'],
    queryFn: () => fetchFlightStores()
  })

  const {
    mutate: updateZonesMutation,
    isPending: isLoadingZones,
    isError: isErrorLoadingZones
  } = useMutation({
    mutationFn: (storeIds: number[]) => updateAvailableZones(storeIds),
    onSuccess: (data) => {
      setZonesResponse(data)
    }
  })

  const {
    mutate: updateTargetPreviewMutation,
  } = useMutation({
    mutationFn: ({
       storeIds,
       zoneIds,
       placementIds
     }: {
      storeIds: number[]
      zoneIds: number[] | null
      placementIds: number[] | null
    }) => updateTargetingPreview(storeIds, zoneIds, placementIds),
    onSuccess: (data) => {
      setPreviewResponse(data)
    }
  })

  const { mutate: saveTargetsMutation, isPending: isSavingTargets } =
    useMutation({
      mutationFn: () => saveFlightTargets(Number(flightId), saveInput),
      onSuccess: () => {
        setOpenSnackbar({ open: true, message: 'Targets saved successfully' })
        navigate(`/campaign/${campaignId}`)
      },
      onError: (error: AxiosError) => {
        const errorResponse = error.response?.data as ErrorResponse
        const message =
          errorResponse?.context?.map((text) => text).join(', ') ||
          'Error saving targets'
        setOpenSnackbar({ open: true, message })
        setConfirmationModal({
          open: false,
          message: ''
        })
      }
    })

  const { mutate: saveTargetsCreativeMutation } = useMutation({
    mutationFn: () => saveFlightTargets(Number(flightId), saveInput),
    onSuccess: () => {
      setOpenSnackbar({ open: true, message: 'Creative successfully added!' })
      refetchCampaign()
    },
    onError: (error: AxiosError) => {
      const errorResponse = error.response?.data as ErrorResponse
      const message =
        errorResponse?.context?.map((text) => text).join(', ') ||
        'Error saving targets'
      setOpenSnackbar({ open: true, message })
    }
  })

  const { mutate: updateCreativeMutation, isPending: isPendingCreativeUpdate } =
    useMutation({
      mutationFn: (updatedCreative: CreativeUpdateInput) =>
        updateCreative(updatedCreative),
      onError: (error: AxiosError) => {
        const errorResponse = error.response?.data as ErrorResponse
        const message =
          errorResponse?.context?.map((text) => text).join(', ') ||
          'Error updating creative'
        setCreativeAutoUpdateError(true)
        setOpenSnackbar({ open: true, message })
      }
    })

  const { mutate: deleteCreativeMutation, isPending: isPendingCreativeDelete } =
    useMutation({
      mutationFn: (creativeId: number) => deleteCreative(creativeId),
      onError: (error: AxiosError) => {
        const errorResponse = error.response?.data as ErrorResponse
        const message =
          errorResponse?.context?.map((text) => text).join(', ') ||
          'Error deleting creative'
        setCreativeAutoUpdateError(true)
        setOpenSnackbar({ open: true, message })
      }
    })

  const {
    data: storePropertyList,
    isPending: isLoadingStoreProps,
    isError: isErrorStoreProps
  } = useQuery({
    queryKey: ['storeProperties'],
    queryFn: fetchStoreProperties
  })

  const { data: contentCategories } = useQuery({
    queryKey: ['contentCategories'],
    queryFn: fetchContentCategories,
    retry: false,
    enabled: isGtvUser
  })

  const campaignBannerList = useMemo(() => {
    if (!flightStores) return []

    const idToBanner = new Map<number, { id: number; name: string }>()
    flightStores.forEach((store) => {
      const banner = store.banner
      if (banner && !idToBanner.has(banner.id)) {
        idToBanner.set(banner.id, { id: banner.id, name: banner.name })
      }
    })

    return Array.from(idToBanner.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [flightStores])

  const campaignStateList = useMemo(() => {
    if (!flightStores) return []

    const states = new Set<string>()
    flightStores.forEach((store) => {
      const state = store.address.state?.trim()
      if (state) states.add(state)
    })

    return Array.from(states).sort((a, b) => a.localeCompare(b))
  }, [flightStores])

  // Maps for rendering selected filter labels
  const bannerIdToName = useMemo(() => {
    const map = new Map<number, string>()
    campaignBannerList.forEach((b: { id: number; name: string }) => {
      map.set(b.id, b.name)
    })
    return map
  }, [campaignBannerList])

  // For filling in existing targeting
  useEffect(() => {
    if (fetchedCampaign && flightStores) {
      const currentFlight = fetchedCampaign.flights.find(
        (flight) => flight.id === Number(flightId)
      )
      if (currentFlight) {
        setCurrentFlight(currentFlight)

        const hasExistingTargeting =
          currentFlight?.targeting.stores.length > 0 ||
          currentFlight?.targeting.placements.length > 0 ||
          currentFlight?.targeting.zones.length > 0
        if (hasExistingTargeting) {
          const validStores = flightStores.filter((store) =>
            currentFlight.targeting.stores.some(
              (targetStore) => targetStore.id === store.id
            )
          )
          const selectedStoreIds = validStores.map((store) => store.id)
          setSelectedStores(selectedStoreIds)
          setCurrentGridSelection(selectedStoreIds)
          validStores.length > 0 && setShowSelected(true)
          setPreviewZones(currentFlight.targeting.zones.map((z) => z.id))
          setPreviewPlacements(currentFlight.targeting.placements.map((p) => p.id))

          // Auto-select saved filters (banner_ids, states)
          const savedFilterBanners = currentFlight.targeting.filters?.banner_ids || []
          const savedFilterStates = currentFlight.targeting.filters?.states || []
          if (savedFilterBanners.length > 0) {
            setValue('selectedBanners', savedFilterBanners)
          }
          if (savedFilterStates.length > 0) {
            setValue('selectedStates', savedFilterStates)
          }
        }
      }
    }
  }, [fetchedCampaign, flightStores])

  useEffect(() => {
    if (selectedStores.length > 0) {
      updateZonesMutation(selectedStores)
      setPreviewPlacements(null)
      setPreviewZones(null)
    }
  }, [selectedStores])

  const handlePreviewTargetsChange = ({
    previewZones,
    previewPlacements
  }: {
    previewZones: number[]
    previewPlacements: number[]
  }) => {
    setPreviewZones(previewZones)
    setPreviewPlacements(previewPlacements)
  }

  useEffect(() => {
    const zoneLength = previewZones ? previewZones.length : 0
    const placementLength = previewPlacements ? previewPlacements.length : 0
    if (zoneLength === 0 && placementLength === 0) {
      setPreviewZones(null)
      setPreviewPlacements(null)
      setPreviewResponse(null)
    }

    updateTargetPreviewMutation({
      storeIds: selectedStores,
      zoneIds: previewZones,
      placementIds: previewPlacements
    })
  }, [previewZones, previewPlacements])

  const handleZoneSelectionChange = (previewInput: CreativePreviewInput) => {
    setTargetingInput(previewInput)
  }

  const handleSaveTargets = () => {
    if (outdatedCreatives.length > 0) {
      setConfirmationModal({
        open: true,
        message:
          'Creative assets for formats that are no longer targeted will stop playing immediately. Creative assets that have not started playing will be deleted.\n\nAre you sure you want to save your changes?'
      })
    } else {
      saveTargetsMutation()
    }
  }

  const handleOutdatedAndSave = () => {
    // Close the confirmation modal immediately when user clicks Save
    setConfirmationModal({
      open: false,
      message: ''
    })

    outdatedCreatives.forEach((outdated) => {
      const timeZone = currentFlight?.time_zone
      const isPlaying = dayjs(outdated.calculated_duration.starts_at)
        .tz(timeZone)
        .isBefore(dayjs().tz(timeZone))
      const hasEnded = dayjs(outdated.calculated_duration.ends_at)
        .tz(timeZone)
        .isBefore(dayjs().tz(timeZone))

      // If the creative has ended, do not update or delete it
      if (!hasEnded) {
        if (isPlaying) {
          const selectedContentCategory = contentCategories?.find((c) => c.name === outdated.content_category)
          updateCreativeMutation({
            ...outdated,
            starts_at: outdated.duration.starts_at,
            ends_at: dayjs().tz(timeZone).add(2, 'minutes').utc().format(),
            content_category_id: selectedContentCategory?.id as number
          })
        } else {
          deleteCreativeMutation(outdated.id)
        }
      }
    })

    if (!creativeAutoUpdateError) {
      saveTargetsMutation()
    }
  }

  // Build save input with filters
  const saveInput: FlightTargetSaveInput | null = useMemo(() => {
    if (!targetingInput) return null
    return {
      ...targetingInput,
      targeting_filter: {
        banner_ids: selectedBanners,
        states: selectedStates
      }
    }
  }, [targetingInput, selectedBanners, selectedStates])

  const disableSave = !saveInput || (saveInput.placements.length === 0 && saveInput.zones.length === 0) || isSavingTargets

  const columns = [
    { field: 'id', headerName: 'Store ID', flex: 0.5, sortable: false },
    { field: 'name', headerName: 'Store Name', flex: 0.5, sortable: true },
    {
      field: 'banner',
      headerName: 'Banner',
      flex: 1,
      valueGetter: (params: { row: Store }) => params.row.banner.name,
      sortable: false
    },
    {
      field: 'address',
      headerName: 'City, State',
      flex: 1,
      valueGetter: (params: { row: Store }) => {
        const city = params.row.address.city || ''
        const state = params.row.address.state || ''
        return `${city}${city && state ? ', ' : ''}${state}`
      },
      sortable: false
    }
  ]

  const header = (
    <Typography
      variant='heading-8'
      sx={{
        width: '65%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        paddingBottom: '20px'
      }}
    >
      <NewFlightIcon
        fontSize='small'
        sx={{
          color: theme.palette['base-black'],
          marginRight: theme.space['20'],
          height: '28px',
          width: '28px'
        }}
      />
      Flight Targets
    </Typography>
  )

  const subheaders = (
    <>
      <Typography variant='h6' sx={{ marginBottom: theme.space['32'] }}>
        Targeting
      </Typography>
      <Typography variant='body-8' sx={{ marginBottom: theme.space['12'] }}>
        Stores
      </Typography>
    </>
  )

  const footer = (
    <Box
      sx={{
        marginTop: theme.space['40'],
        display: 'flex',
        justifyContent: 'space-between',
        gap: theme.space['20']
      }}
    >
      <Button
        variant='contained'
        color='secondary'
        sx={{ width: '25%', height: '56px' }}
        onClick={() => navigate(`/campaign/${campaignId}`)}
      >
        <Typography variant='ui-3'>Cancel</Typography>
      </Button>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '50%'
        }}
      >
        <Button
          variant='contained'
          color='secondary'
          sx={{ width: '47%', height: '56px' }}
          onClick={() => {
            navigate(`/campaign/${campaignId}/flight/${flightId}`)
          }}
        >
          <Typography variant='ui-3'>Back</Typography>
        </Button>
        <Button
          data-testid='flight-save-button'
          color='primary'
          variant='contained'
          sx={{ width: '47%', height: '56px' }}
          onClick={handleSaveTargets}
          disabled={disableSave}
        >
          <Typography variant='ui-3'>Save & Finish</Typography>
        </Button>
      </Box>
    </Box>
  )

  const normalizeString = (str: string) => {
    return str.toLowerCase().replace(/,/g, '').replace(/\s+/g, ' ').trim()
  }

  const filterStores = (stores: Store[], hideDeselected: boolean, searchQuery: string) => {
    let result = stores

    // Step 1: Apply 'Hide deselected stores' filter first
    if (hideDeselected) {
      result = result.filter(store => selectedStores.includes(store.id))
    }

    // Step 2: Apply banner and state filters
    if (selectedBanners && selectedBanners.length > 0) {
      const bannerSet = new Set(selectedBanners)
      result = result.filter(store => bannerSet.has(store.banner.id))
    }
    if (selectedStates && selectedStates.length > 0) {
      const stateSet = new Set(selectedStates)
      result = result.filter(store => stateSet.has((store.address.state || '').trim()))
    }

    // Step 3: Apply search filter on the resulting set
    if (searchQuery.trim()) {
      const terms = detectDelimiterAndSplit(searchQuery)
      result = result.filter(store => {
        return terms.some((term) => {
          const normalizedTerm = normalizeString(term)
          const isIdMatch = store.id.toString() === normalizedTerm
          const isPartialMatch =
            normalizeString(store.name).includes(normalizedTerm) ||
            normalizeString(store.banner.name).includes(normalizedTerm) ||
            getLocationString(store).includes(normalizedTerm) ||
            store.id.toString().includes(normalizedTerm)

          return exactIdMatch ? isIdMatch : isIdMatch || isPartialMatch
        })
      })
    }

    return result
  }
  const getLocationString = (store: Store) => {
    const city = store.address.city || ''
    const stateAbbreviation = store.address.state || ''
    const state = US_STATES[stateAbbreviation as keyof typeof US_STATES] || ''
    return normalizeString(`${city} ${stateAbbreviation} ${state}`)
  }

  const detectDelimiterAndSplit = (search: string) => {
    const trimmed = search.trim()
    const commaCount = (trimmed.match(/,/g) || []).length
    const newlineCount = (trimmed.match(/\n/g) || []).length

    let delimiter
    if (commaCount === 0 && newlineCount === 0) {
      return [trimmed]
    } else if (commaCount >= newlineCount) {
      delimiter = ','
    } else {
      delimiter = '\n'
    }

    return trimmed
      .split(delimiter)
      .map((t) => normalizeString(t))
      .filter((t) => t.length > 0)
  }

  const filteredStores = useMemo(() => {
    if (!flightStores) return []
    return filterStores(flightStores, showSelected, searchTerm)
  }, [searchTerm, flightStores, showSelected, selectedStores, exactIdMatch, selectedBanners, selectedStates, selectedPropertyValues])

  React.useEffect(() => {
    const initialPanels: string[] = []
    if (isBannerExpanded) {
      initialPanels.push('panel-banner')
    }
    if (isStateExpanded) {
      initialPanels.push('panel-state')
    }
    // Add panels for properties that have selected values
    // if (storePropertyList && selectedPropertyValues.length > 0) {
    //   storePropertyList.forEach((property) => {
    //     if (
    //       property.values.some((value) =>
    //         selectedPropertyValues.includes(value.id)
    //       )
    //     ) {
    //       initialPanels.push(`panel-${property.id}`)
    //     }
    //   })
    // }
    setExpandedPanels(initialPanels)
  }, [isBannerExpanded, isStateExpanded])

  const handleAccordionChange =
  (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanels((prev) =>
      isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
    )
  }

  const handleValueChange = (valueId: number) => {
    const newSelectedValues = selectedPropertyValues.includes(valueId)
      ? selectedPropertyValues.filter((id: number) => id !== valueId)
      : [...selectedPropertyValues, valueId]
    setValue('selectedPropertyValues', newSelectedValues)
  }

  const handleBannerChange = (bannerId: number) => {
    const newSelectedBanners = selectedBanners.includes(bannerId)
      ? selectedBanners.filter((id: number) => id !== bannerId)
      : [...selectedBanners, bannerId]
    setValue('selectedBanners', newSelectedBanners)
  }

  const handleStateChange = (state: string) => {
    const newSelectedStates = selectedStates.includes(state)
      ? selectedStates.filter((s: string) => s !== state)
      : [...selectedStates, state]
    setValue('selectedStates', newSelectedStates)
  }

  // Update selectedStores when banner/state filters change
  useEffect(() => {
    if (!flightStores) return

    // Filter selectedStores to only include stores that match current banner/state filters
    const filteredSelectedStores = selectedStores.filter(storeId => {
      const store = flightStores.find(s => s.id === storeId)
      if (!store) return false

      // Check banner filter
      if (selectedBanners.length > 0 && !selectedBanners.includes(store.banner.id)) {
        return false
      }

      // Check state filter
      if (selectedStates.length > 0 && !selectedStates.includes((store.address.state || '').trim())) {
        return false
      }

      return true
    })
    
    // Only update if the filtered list is different from current selectedStores
    if (filteredSelectedStores.length !== selectedStores.length || 
        !filteredSelectedStores.every(id => selectedStores.includes(id))) {
      setSelectedStores(filteredSelectedStores)
    }
  }, [selectedBanners, selectedStates, flightStores, selectedStores])

  // Handle user selection changes directly in the grid handler
  const handleGridSelectionChange = (newSelection: GridRowSelectionModel) => {
    const selectionArray = newSelection as number[]
    
    // Only update if the selection actually changed
    const currentSelection = currentGridSelection.sort()
    const newSelectionSorted = selectionArray.sort()
    const hasChanged = currentSelection.length !== newSelectionSorted.length || 
      currentSelection.some((id, index) => id !== newSelectionSorted[index])
    
    if (hasChanged) {
      setCurrentGridSelection(selectionArray)

      // Determine which rows are currently visible in the grid
      const visibleStoreIds = (filteredStores || []).map((s) => s.id)

      // Preserve selections that are currently hidden by filters/search
      const preservedHiddenSelections = (selectedStores as number[]).filter(
        (id) => !visibleStoreIds.includes(id)
      )

      // New selection is the union of preserved hidden selections and the user-selected visible rows
      const nextSelectedStores = Array.from(
        new Set<number>([...preservedHiddenSelections, ...selectionArray])
      )

      // Only update if changed compared to previous selectedStores
      const isSameLength = nextSelectedStores.length === selectedStores.length
      const hasSameMembers = isSameLength && nextSelectedStores.every((id) => selectedStores.includes(id))
      if (!hasSameMembers) {
        setSelectedStores(nextSelectedStores)
      }
    }
  }

  const content = (
    <Box>
      {header}
      {isSavingTargets && <CircularLoader />}
      <Box
        sx={{
          borderRadius: theme.borders['rounded-16'],
          padding: theme.space['40'],
          backgroundColor: theme.palette['base-white'],
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {subheaders}
          {currentFlight?.status !== CampaignStatus.Completed && (
            <Box sx={{ marginTop: theme.space['4'] }}>
              <Button
                id='filter-button'
                aria-controls={anchorEl ? 'filter-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={anchorEl ? 'true' : undefined}
                endIcon={<ArrowDropDown sx={{ color: theme.palette['sage-400'] }} />}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                disabled={isLoadingStoreProps || isErrorStoreProps}
                sx={{
                  display: 'flex',
                  width: '240px',
                  border: '1px solid',
                  borderRadius: '6px',
                  borderColor: theme.palette['sage-200'],
                  justifyContent: 'space-between',
                  height: '56px',
                  backgroundColor: theme.palette['base-white'],
                  textTransform: 'none'
                }}
              >
                <Typography variant='body-10' sx={{ color: theme.palette['sage-200'] }}>
                  Filters
                </Typography>
              </Button>
              <Popper
                id='filter-menu'
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement='bottom-start'
                sx={{ zIndex: 1300 }}
                modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
              >
                <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      maxHeight: '400px',
                      borderRadius: '6px',
                      marginTop: '1px',
                      overflow: 'auto',
                      border: '1px solid',
                      borderColor: theme.palette['sage-200'],
                      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                      backgroundColor: theme.palette['base-white']
                      
                    }}
                  >
                    {isLoadingStoreProps && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          padding: theme.space['16']
                        }}
                      >
                        <CircularProgress sx={{ color: theme.palette['accent-500'] }} size={20} />
                      </Box>
                    )}
                    {isErrorStoreProps && !isLoadingStoreProps && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          padding: theme.space['16']
                        }}
                      >
                        <Typography variant='body-11'>Error loading filters</Typography>
                      </Box>
                    )}
                    {!isLoadingStoreProps && !isErrorStoreProps && (
                      <TargetingFilters
                        campaignBannerList={campaignBannerList}
                        campaignStateList={campaignStateList}
                        storePropertyList={storePropertyList}
                        expandedPanels={expandedPanels}
                        handleBannerChange={handleBannerChange}
                        handleStateChange={handleStateChange}
                        handleValueChange={handleValueChange}
                        handleAccordionChange={handleAccordionChange}
                      />
                    )}
                  </Box>
                </ClickAwayListener>
              </Popper>
            </Box>
            )}
      
      {/* Filter Pills Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: theme.space['8'],
          marginBottom: theme.space['12']
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.space['8'] }}>
          {selectedBanners.map((id) => (
            <FilterPill
              key={`banner-pill-${id}`}
              label="Banner"
              value={bannerIdToName.get(id) || `Banner ${id}`}
              onRemove={() => setValue('selectedBanners', selectedBanners.filter((b) => b !== id))}
            />
          ))}
          {selectedStates.map((state) => (
            <FilterPill
              key={`state-pill-${state}`}
              label="State"
              value={state}
              onRemove={() => setValue('selectedStates', selectedStates.filter((s) => s !== state))}
            />
          ))}
          {selectedPropertyValues.map((value) => {
            const property = storePropertyList?.find((p) => p.values.some((v) => v.id === value))
            const propertyValue = property?.values.find((v) => v.id === value)
            return (
              <FilterPill
                key={`property-pill-${value}`}
                label={property?.name || 'Property'}
                value={propertyValue?.name || `Value ${value}`}
                onRemove={() => setValue('selectedPropertyValues', selectedPropertyValues.filter((v) => v !== value))}
              />
            )
          })}
        </Box>
        {(selectedBanners.length > 0 || selectedStates.length > 0) && (
          <FilterPill
            label="Clear All"
            onClick={() => {
              setValue('selectedBanners', [])
              setValue('selectedStates', [])
            }}
            showCloseIcon={false}
          />
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.space['20'],
          marginBottom: theme.space['12']
        }}
      >
        <TextField
          variant='outlined'
          placeholder='Search by Store ID, Name, Banner, City or State'
          multiline
          maxRows={10}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
          }}
          sx={{
            flex: 2,
            '& .MuiInputBase-input': {
              lineHeight: '1.25'
            }
          }}
        />
          <Box display='flex' flexDirection='column' alignItems='flex-start'>
            <FormControlLabel
              control={
                <Switch
                  onChange={(e) => {
                    setExactIdMatch(e.target.checked)
                  }}
                  checked={exactIdMatch}
                />
              }
              label='Exact Match GTV Id Search'
              labelPlacement='start'
              sx={{
                paddingLeft: theme.space['12'],
                height: '40px',
                marginRight: 0,
                width: '100%',
                justifyContent: 'space-between',
                borderBottom: `1px solid ${theme.palette['sage-200']}`,
                '& .MuiFormControlLabel-label': {
                  fontSize: theme.typography['body-2'].fontSize,
                  fontWeight: theme.typography['body-2'].letterSpacing,
                  textTransform: 'uppercase'
                }
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  onChange={(e) => {
                    setShowSelected(e.target.checked)
                    // Clear search when toggling to ensure all selected stores are visible
                    setSearchTerm('')
                  }}
                  checked={showSelected}
                />
              }
              label='Hide deselected stores'
              labelPlacement='start'
              sx={{
                paddingLeft: theme.space['12'],
                height: '40px',
                width: '100%',
                justifyContent: 'space-between',
                marginRight: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: theme.typography['body-2'].fontSize,
                  fontWeight: theme.typography['body-2'].letterSpacing,
                  textTransform: 'uppercase'
                }
              }}
            />
          </Box>
        </Box>
        <DataGrid
          rows={filteredStores ?? []}
          columns={columns}
          rowSelectionModel={selectedStores}
          onRowSelectionModelChange={handleGridSelectionChange}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeader': {
              fontSize: theme.typography['body-2'].fontSize,
              color: theme.palette['sage-500'],
              textTransform: 'uppercase',
              fontWeight: '800'
            },
            '& .MuiDataGrid-cell': {
              fontSize: theme.typography['body-10'].fontSize
            }
          }}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          checkboxSelection
          disableRowSelectionOnClick
          disableColumnMenu
        />
        <FlightZones
          selectedStores={selectedStores}
          availableZones={zonesResponse?.zones ?? []}
          isLoading={isLoadingZones}
          isError={isErrorLoadingZones}
          onSelectionChange={handleZoneSelectionChange}
          onPreviewZonesChange={setPreviewZones}
          onPreviewPlacementsChange={setPreviewPlacements}
          onPreviewTargetsChange={handlePreviewTargetsChange}
          flight={fetchedCampaign?.flights.find(
            (flight) => flight.id === Number(flightId)
          )}
          savedZones={
            currentFlight?.targeting.zones.map((zone) => zone.id) ?? []
          }
          savedPlacements={
            currentFlight?.targeting.placements.map(
              (placement) => placement.id
            ) ?? []
          }
          saveTargetsMutation={() => saveTargetsCreativeMutation()}
          onOutdatedCreatives={setOutdatedCreatives}
          campaignStatus={fetchedCampaign?.status || CampaignStatus.Draft}
          campaignContentCategory={fetchedCampaign?.content_category}
          isGtvUser={isGtvUser}
        />
      </Box>
      {footer}
    </Box>
  )

  return (
    <FormProvider {...formMethods}>
    <Box>
      <Snackbar
        open={openSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({ open: false, message: '' })}
        message={openSnackbar.message}
      />
      <ConfirmationModal
        title='Confirm Update'
        open={confirmationModal.open}
        onClose={() => setConfirmationModal({ open: false, message: '' })}
        onConfirm={handleOutdatedAndSave}
        message={confirmationModal.message}
        isLoading={isPendingCreativeUpdate || isPendingCreativeDelete}
        confirmButtonText='Save'
      />
      <TwoColumnLayout
        barPosition='right'
        sidebar={
          <FlightSidebar
            campaign={fetchedCampaign}
            previewResponse={previewResponse}
            selectedStores={selectedStores}
          />
        }
        content={
          <>
            {isLoadingStores && (
              <Box>
                {header}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <CircularProgress
                    sx={{ color: theme.palette['accent-500'] }}
                  />
                </Box>
              </Box>
            )}
            {isErrorLoadingStores && (
              <Box>
                {header}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant='body-11'>
                    Error loading stores
                  </Typography>
                </Box>
              </Box>
            )}
            {!flightStores && !isLoadingStores && (
              <Box>
                {header}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant='body-11'>No stores found</Typography>
                </Box>
              </Box>
            )}
            {isSuccessLoadingStores && content}
          </>
        }
      />
    </Box>
    </FormProvider>
  )
}

export default FlightTargets
